import clsx from 'clsx'
import Link from 'next/link'
import React, { useState } from 'react'

import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text'
import { Badge } from '@wartek-id/badge'

import { GATrackEvent } from 'utils/googleAnalytics'
import { cacheImage } from 'app/PelatihanMandiriPage/helper'
import { useTopicCardTooltip } from 'app/PelatihanMandiriPage/hooks/useTopicCardTooltip'

import type { Topic } from 'types/microlearning'

const MAX_DESCRIPTION_LENGTH = 150

interface TopicCardProps extends Omit<Topic, 'headerImage'> {
  topicId: string | number
  isFetchingProgress: boolean
  image?: string
  active?: boolean
  className?: string
  multiColumnDescription?: boolean
}

const TopicCard = ({
  topicId,
  name,
  totalModule,
  description,
  href,
  image,
  isFetchingProgress = false,
  badge = undefined,
  active = true,
  className,
  multiColumnDescription = false,
}: Omit<TopicCardProps, 'id' | 'duration'>) => {
  const [imgLoaded, setImgLoaded] = useState(false)
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const descriptionCharacters = Array.from(description)
  const hasLongDescription =
    descriptionCharacters.length > MAX_DESCRIPTION_LENGTH
  const descriptionPreview = hasLongDescription
    ? `${descriptionCharacters.slice(0, MAX_DESCRIPTION_LENGTH).join('')}…`
    : description
  const { tooltipText, titleTextRef, descriptionRef, descriptionPreviewRef } =
    useTopicCardTooltip({
      name,
      description,
      multiColumnDescription,
      hasLongDescription,
    })

  return (
    <div
      data-testid="topic-card"
      className={clsx(
        'relative flex flex-col p-3 pl-4 last:mb-0 rounded overflow-hidden border box-border',
        multiColumnDescription && 'lg:h-auto lg:min-h-[182px]',
        active ? 'border-primary bg-blue-20' : 'bg-white border-subdued',
        className
      )}
    >
      <div className="flex flex-1 gap-4">
        <div className="flex min-w-0 flex-auto flex-col">
          <div className="mb-1">
            <Link href={href}>
              {/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */}
              <Text
                data-topik-id={topicId}
                onClick={() => {
                  GATrackEvent({
                    eventName: 'learning_activity',
                    eventParams: {
                      event_trigger: 'topik_click_from_list',
                      action_type: 'choose_topik',
                      meta: JSON.stringify({
                        topic_id: topicId,
                      }),
                    },
                    sendDefaultParams: true,
                  })
                }}
                className="overlay-link"
                title={tooltipText}
                href={href}
                as="a"
                variant="heading-sm"
              >
                <span
                  ref={titleTextRef}
                  className="line-clamp-3 md:line-clamp-2"
                >
                  {name}
                </span>
              </Text>
            </Link>
          </div>
          <div
            data-testid="topic-description"
            ref={descriptionRef}
            className={clsx(
              'break-words',
              multiColumnDescription && hasLongDescription && 'lg:hidden'
            )}
          >
            <Text variant="body-sm" color="subdued">
              {hasLongDescription && !isDescriptionExpanded
                ? descriptionPreview
                : description}
            </Text>
          </div>
          {hasLongDescription && (
            <button
              type="button"
              className={clsx(
                'relative z-10 mt-1 self-start text-sm font-semibold text-blue-60',
                multiColumnDescription && 'lg:hidden'
              )}
              aria-expanded={isDescriptionExpanded}
              onClick={(event) => {
                event.stopPropagation()
                setIsDescriptionExpanded((expanded) => !expanded)
              }}
            >
              {isDescriptionExpanded
                ? 'Lihat lebih sedikit'
                : 'Lihat selengkapnya'}
            </button>
          )}
          {multiColumnDescription && hasLongDescription && (
            <div
              ref={descriptionPreviewRef}
              className="hidden lg:block break-words"
              data-testid="topic-description-preview"
            >
              <Text variant="body-sm" color="subdued">
                {descriptionPreview}
              </Text>
            </div>
          )}
          <div className="mt-auto flex items-center pt-3">
            <Icon
              as="i"
              color="default"
              style={{ fontSize: '16px' }}
              fontSize="sm"
            >
              library_books
            </Icon>
            <div className="ml-2 leading-3">
              <Text as="span" variant="helper-bold" color="subdued">
                {totalModule} modul
              </Text>
            </div>
          </div>
        </div>
        <div
          className={clsx(
            'relative flex-grow-0 flex-shrink-0 w-16 h-16 rounded overflow-hidden',
            (!image || !image.length) &&
              'flex items-center justify-center bg-expressive-dark-blue'
          )}
        >
          {!!image && image.length >= 0 ? (
            <>
              <img
                className={clsx(
                  'object-cover object-center',
                  imgLoaded ? 'w-16 h-16' : 'h-0'
                )}
                src={cacheImage(image)}
                alt={name}
                onLoad={() => {
                  setImgLoaded(true)
                }}
                loading="lazy"
              />
              {!imgLoaded && <Shimmer height="4rem" width="4rem" />}
            </>
          ) : (
            <Icon
              as="i"
              color="inverse"
              className="block ml-auto"
              fontSize="lg"
              title="Topik"
            >
              library_books
            </Icon>
          )}
        </div>
      </div>
      {isFetchingProgress && (
        <Shimmer
          height={16}
          width={140}
          className="rounded-lg mt-3"
          aria-busy={isFetchingProgress ? 'true' : 'false'}
          role="alert"
        />
      )}
      {!isFetchingProgress && !!badge && !!badge.state && (
        <Badge data-testid="badge" className="mt-3" variant={badge?.type}>
          {badge?.text}
        </Badge>
      )}
    </div>
  )
}

export default TopicCard
